import type { CurrentUser } from "@zhiyue/core";
import { getOpenPlatformToken } from "./bff/open-platform.api";

const OPEN_PLATFORM_TOKEN_STORAGE_KEY = "dataIntake.openPlatformAccessToken";
const TOKEN_REFRESH_BUFFER_MS = 60_000;

export type OpenPlatformTokenResult = {
  accessToken: string;
  expiresAt: number;
};

export type OpenPlatformTokenContext = Pick<CurrentUser, "memberUuid"> & {
  organizationUuid?: string;
};

type CachedOpenPlatformToken = OpenPlatformTokenResult & {
  cacheKey: string;
};

let pendingRequest:
  | { cacheKey: string; promise: Promise<OpenPlatformTokenResult> }
  | undefined;
let tokenRequestGeneration = 0;

const normalizeText = (value: unknown) => String(value ?? "").trim();

const getCacheKey = (currentUser: OpenPlatformTokenContext) =>
  [
    normalizeText(currentUser.organizationUuid),
    normalizeText(currentUser.memberUuid),
  ].join("|");

const readCachedToken = (cacheKey: string): OpenPlatformTokenResult | null => {
  try {
    const rawValue = sessionStorage.getItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const value = JSON.parse(rawValue) as CachedOpenPlatformToken;
    if (
      value.cacheKey !== cacheKey ||
      typeof value.accessToken !== "string" ||
      !value.accessToken.trim() ||
      typeof value.expiresAt !== "number" ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
    ) {
      sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
      return null;
    }

    return {
      accessToken: value.accessToken,
      expiresAt: value.expiresAt,
    };
  } catch {
    sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    return null;
  }
};

export const clearOpenPlatformToken = (): void => {
  tokenRequestGeneration += 1;
  pendingRequest = undefined;
  sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
};

export const getCachedOpenPlatformToken = (
  currentUser: OpenPlatformTokenContext
): OpenPlatformTokenResult | null => readCachedToken(getCacheKey(currentUser));

export const fetchOpenPlatformToken = async (
  currentUser: OpenPlatformTokenContext
): Promise<OpenPlatformTokenResult> => {
  const organizationUuid = normalizeText(currentUser.organizationUuid);
  const memberUuid = normalizeText(currentUser.memberUuid);
  if (!organizationUuid || !memberUuid) {
    clearOpenPlatformToken();
    throw new Error("当前会话缺少开放平台认证信息");
  }

  const cacheKey = getCacheKey({ organizationUuid, memberUuid });
  const cachedToken = readCachedToken(cacheKey);
  if (cachedToken) {
    return cachedToken;
  }

  if (pendingRequest?.cacheKey === cacheKey) {
    return pendingRequest.promise;
  }

  if (pendingRequest) {
    tokenRequestGeneration += 1;
    pendingRequest = undefined;
  }

  const requestGeneration = tokenRequestGeneration;
  let promise: Promise<OpenPlatformTokenResult>;
  promise = getOpenPlatformToken({ skipErrorHandler: true }).then((result) => {
    if (
      typeof result?.accessToken !== "string" ||
      !result.accessToken.trim() ||
      typeof result.expiresAt !== "number" ||
      !Number.isFinite(result.expiresAt) ||
      result.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
    ) {
      throw new Error("开放平台 Token 响应无效");
    }

    if (
      requestGeneration === tokenRequestGeneration &&
      pendingRequest?.promise === promise
    ) {
      sessionStorage.setItem(
        OPEN_PLATFORM_TOKEN_STORAGE_KEY,
        JSON.stringify({ ...result, cacheKey })
      );
    }

    return result;
  });

  pendingRequest = { cacheKey, promise };
  try {
    return await promise;
  } finally {
    if (pendingRequest?.promise === promise) {
      pendingRequest = undefined;
    }
  }
};
